import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { mieterstromInvoicingApiWithoutCamelization } from "../../../api/subApis/mieterstromInvoicingApi";
import type { Contract } from "../../DeliveryOverview/DeliveryOverview.types";

export function useContract(contractId: string | undefined) {
  const queryClient = useQueryClient();
  const {
    isLoading: isContractDataLoading,
    error: contractLoadingError,
    data: contract
  } = useQuery({
    queryFn: contractId ? () => fetchContract(contractId) : skipToken,
    queryKey: mieterstromInvoicingApiWithoutCamelization.contract.key(
      contractId || ""
    ),
    enabled: typeof contractId !== "undefined",
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  async function createContract(contract: Contract) {
    const endpointUrl =
      mieterstromInvoicingApiWithoutCamelization.contracts.url(
        contract.project
      );

    try {
      const response = await apiWithoutCamelization.post<Contract>(
        endpointUrl,
        contract
      );
      queryClient.invalidateQueries({
        queryKey: mieterstromInvoicingApiWithoutCamelization.contracts.key(
          contract.project
        )
      });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function updateContract(contract: Contract) {
    const endpointUrl = mieterstromInvoicingApiWithoutCamelization.contract.url(
      contract.id
    );

    try {
      const response = await apiWithoutCamelization.put<Contract>(
        endpointUrl,
        contract
      );
      queryClient.invalidateQueries({
        queryKey: mieterstromInvoicingApiWithoutCamelization.contract.key(
          contract.id
        )
      });
      queryClient.invalidateQueries({
        queryKey: mieterstromInvoicingApiWithoutCamelization.contracts.key(
          contract.project
        )
      });
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  return {
    isContractDataLoading,
    contractLoadingError,
    contract,
    createContract,
    updateContract
  };
}

async function fetchContract(contractId: string) {
  const endpointUrl =
    mieterstromInvoicingApiWithoutCamelization.contract.url(contractId);
  const response = await apiWithoutCamelization.get<Contract>(endpointUrl);

  return response.data;
}
